// frontend/src/services/timesheet.service.ts - COMPLETE FIX WITH ALL LOGIC

import {
  collection,
  doc,
  query,
  where,
  getDocs,
  getDoc
} from 'firebase/firestore';
import { db } from './firebase';
import * as XLSX from 'xlsx';

export interface TimesheetEmployee {
  id: string;
  name: string;
  pin: string;
  designation: string;
  department: string;
  shiftStart: string;
  shiftEnd: string;
  workTiming: string;
  
  isNightShift: boolean;
  nightShiftStartTime?: string;
  nightShiftEndTime?: string;
  shiftType: 'day' | 'night';
}

export interface DailyTimesheetRecord {
  date: Date;
  dateString: string;
  checkIn: string | null;
  checkOut: string | null;
  checkOutLocation?: string;
  breakTime: string;
  noBreakCount: number;
  overtimeSessions: OvertimeSession[];
  remarks: string;
  discrepancy: string;
  regularHours: number;
  overtimeHours: number;
  totalHours: number;
  isIncomplete: boolean;
  overtimeBreakdown?: {
    approvedOT: number;
    nightShiftDefaultOT: number;
    lateCheckoutOT: number;
    noBreakBonus: number;
  };
}

export interface OvertimeSession {
  projectCode: string;
  projectName: string;
  startTime: string;
  endTime: string;
  duration: number;
  status: 'approved' | 'pending' | 'rejected';
}

export interface TimesheetData {
  employee: TimesheetEmployee;
  periodStart: Date;
  periodEnd: Date;
  records: DailyTimesheetRecord[];
  incompleteCheckoutsCount: number;
  summary: {
    totalWorkDays: number;
    totalPresent: number;
    totalAbsent: number;
    totalLeaves: number;
    totalNoBreakDays: number;
    totalOvertimeDays: number;
    totalRegularHours: number;
    totalOvertimeHours: number;
    totalHours: number;
    totalApprovedOT: number;
    totalNightShiftDefaultOT: number;
    totalLateCheckoutOT: number;
    totalNoBreakBonus: number;
    incompleteCheckouts: number;
  };
}

export interface SalaryPeriod {
  startDate: Date;
  endDate: Date;
  label: string;
}

class TimesheetService {
  private pinToUUIDMap = new Map<string, string>();
  private uuidToPinMap = new Map<string, string>();
  private isMapBuilt = false;

  // ========================================================================
  // LOCATION HANDLING - FROM ATTENDANCE SERVICE
  // ========================================================================
  
  private isUUID(str: string): boolean {
    if (!str) return false;
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return UUID_REGEX.test(str) || (str.length > 20 && str.includes('-'));
  }

  private isValidLocationName(name: string): boolean {
    if (!name || name.trim() === '') return false;
    if (this.isUUID(name)) return false;
    
    const INVALID_LOCATION_VALUES = new Set([
      'unknown', 'unknown location', 'unknown location name',
      'null', 'undefined', 'n/a', 'na'
    ]);
    
    return !INVALID_LOCATION_VALUES.has(name.toLowerCase().trim());
  }

  private cleanLocationName(location: string): string {
    if (!location) return 'Unknown';
    
    let cleanedLocation = location.trim();
    cleanedLocation = cleanedLocation.replace(/^location\s+/i, '');
    cleanedLocation = cleanedLocation
      .replace(/\s*\(Polygon\s+Boundary\)\s*/gi, '')
      .replace(/\s*\(polygon\s+boundary\)\s*/gi, '')
      .replace(/\s*polygon\s+boundary\s*/gi, '');
    
    if (cleanedLocation.includes('-')) {
      const parts = cleanedLocation.split('-');
      if (parts.length >= 2) {
        cleanedLocation = `${parts[0]}-${parts[1]}`;
      }
    }
    
    if (cleanedLocation.includes('HEAD OFFICE')) return 'HEAD OFFICE';
    if (cleanedLocation.includes('SITE OFFICE')) return 'SITE OFFICE';
    
    if (cleanedLocation.length > 15) {
      const projectCodeMatch = cleanedLocation.match(/^([A-Z]{1,3}\d{3,4})/);
      if (projectCodeMatch) {
        return projectCodeMatch[1];
      }
      return cleanedLocation.substring(0, 12) + '...';
    }
    
    return cleanedLocation || 'Unknown';
  }

  private extractProjectCodeFromUUID(uuid: string): string {
    if (!uuid) return 'Unknown';
    
    const PROJECT_PATTERNS = [/PJ\d{4}/i, /SO\d{3}/i, /BO\d{3}/i];
    
    for (const pattern of PROJECT_PATTERNS) {
      const match = uuid.match(pattern);
      if (match) {
        return match[0].toUpperCase();
      }
    }
    
    return uuid.substring(0, 8);
  }

  private getLocationDisplayName(record: any): string {
    if (record.checkOutLocationName && this.isValidLocationName(record.checkOutLocationName)) {
      return this.cleanLocationName(record.checkOutLocationName);
    }

    if (record.checkInLocationName && this.isValidLocationName(record.checkInLocationName)) {
      return this.cleanLocationName(record.checkInLocationName);
    }

    if (record.locationName && this.isValidLocationName(record.locationName)) {
      return this.cleanLocationName(record.locationName);
    }

    if (record.location && this.isValidLocationName(record.location)) {
      return this.cleanLocationName(record.location);
    }

    if (record.checkOutLocation && this.isUUID(record.checkOutLocation)) {
      return this.extractProjectCodeFromUUID(record.checkOutLocation);
    }

    if (record.checkInLocation && this.isUUID(record.checkInLocation)) {
      return this.extractProjectCodeFromUUID(record.checkInLocation);
    }

    if (record.location && this.isUUID(record.location)) {
      return this.extractProjectCodeFromUUID(record.location);
    }

    return 'Unknown';
  }

  // ========================================================================
  // DATE UTILITIES
  // ========================================================================
  
  private isToday(date: Date): boolean {
    const today = new Date();
    const dubaiToday = new Date(today.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));
    
    return (
      date.getFullYear() === dubaiToday.getFullYear() &&
      date.getMonth() === dubaiToday.getMonth() &&
      date.getDate() === dubaiToday.getDate()
    );
  }

  // ========================================================================
  // PIN MAPPING
  // ========================================================================
  
  private async buildPinToUUIDMap(): Promise<void> {
    if (this.isMapBuilt) return;

    console.log('🔄 Building PIN <-> UUID mapping...');
    
    try {
      const employeesSnapshot = await getDocs(collection(db, 'employees'));
      
      employeesSnapshot.docs.forEach(doc => {
        const data = doc.data();
        const pin = data.employeePin || data.pin || data.employeeNumber;
        
        if (pin) {
          const cleanPin = pin.toString().trim();
          const pinWithoutZeros = cleanPin.replace(/^0+/, '') || '0';
          
          this.pinToUUIDMap.set(cleanPin, doc.id);
          this.pinToUUIDMap.set(pinWithoutZeros, doc.id);
          this.pinToUUIDMap.set(`0${cleanPin}`, doc.id);
          
          this.uuidToPinMap.set(doc.id, cleanPin);
        }
      });
      
      this.isMapBuilt = true;
      console.log(`✅ Built PIN mapping with ${this.pinToUUIDMap.size} entries`);
    } catch (error) {
      console.error('❌ Error building PIN mapping:', error);
      throw error;
    }
  }

  private getUUIDFromPin(pin: string): string | null {
    if (!pin) return null;
    
    const cleanPin = pin.toString().trim();
    
    if (this.pinToUUIDMap.has(cleanPin)) {
      return this.pinToUUIDMap.get(cleanPin) || null;
    }
    
    const pinWithoutZeros = cleanPin.replace(/^0+/, '') || '0';
    if (this.pinToUUIDMap.has(pinWithoutZeros)) {
      return this.pinToUUIDMap.get(pinWithoutZeros) || null;
    }
    
    return null;
  }

  private extractPinFromId(id: string): string | null {
    if (!id || typeof id !== 'string') return null;
    
    if (/^\d+$/.test(id)) {
      return id.replace(/^0+/, '') || '0';
    }
    
    if (/^EMP\d+$/i.test(id)) {
      const pin = id.replace(/^EMP/i, '');
      return pin.replace(/^0+/, '') || '0';
    }
    
    const match = id.match(/\d+/);
    if (match && match[0].length >= 3 && match[0].length <= 6) {
      return match[0].replace(/^0+/, '') || '0';
    }
    
    return null;
  }

  // ========================================================================
  // SEARCH EMPLOYEE
  // ========================================================================
  
  async searchEmployeeByPin(pin: string): Promise<TimesheetEmployee | null> {
    try {
      console.log('🔍 Searching employee by PIN:', pin);
      
      await this.buildPinToUUIDMap();

      const masterSheetSnapshot = await getDocs(
        collection(db, 'MasterSheet', 'Employee-Data', 'employees')
      );

      const normalizedPin = pin.toLowerCase().trim();
      const numericPin = this.extractPinFromId(pin);

      for (const docSnap of masterSheetSnapshot.docs) {
        const data = docSnap.data();
        const empPin = (data.pin || data.employeeNumber || '').toString().toLowerCase().trim();
        const empId = docSnap.id.toLowerCase();
        const empNumericPin = this.extractPinFromId(empPin) || this.extractPinFromId(docSnap.id);

        if (
          empPin === normalizedPin ||
          empId === `emp${normalizedPin}` ||
          empId === normalizedPin ||
          empNumericPin === numericPin
        ) {
          const workTiming = data.workTiming || '08:00 - 18:00';
          const [shiftStart, shiftEnd] = workTiming.split('-').map((t: string) => t.trim());
          
          const isNightShift = data.isNightShift === true;
          const nightShiftStartTime = data.nightShiftStartTime || '22:00';
          const nightShiftEndTime = data.nightShiftEndTime || '09:00';
          const shiftType = data.shiftType || 'day';
          
          console.log(`✅ Found employee: ${data.employeeName}`);
          console.log(`   - Work Timing: ${workTiming}`);
          console.log(`   - Night Shift: ${isNightShift}`);
          if (isNightShift) {
            console.log(`   - Night Shift Hours: ${nightShiftStartTime} - ${nightShiftEndTime}`);
          }
          
          return {
            id: docSnap.id,
            name: data.employeeName || 'Unknown',
            pin: data.employeeNumber || docSnap.id.replace(/^EMP/i, ''),
            designation: data.designation || 'No designation',
            department: data.department || 'No department',
            shiftStart: shiftStart || '08:00',
            shiftEnd: shiftEnd || '18:00',
            workTiming: workTiming,
            isNightShift: isNightShift,
            nightShiftStartTime: nightShiftStartTime,
            nightShiftEndTime: nightShiftEndTime,
            shiftType: shiftType as 'day' | 'night'
          };
        }
      }

      console.log('❌ Employee not found for PIN:', pin);
      return null;
    } catch (error) {
      console.error('❌ Error searching employee:', error);
      return null;
    }
  }

  // ========================================================================
  // SALARY PERIODS
  // ========================================================================
  
  getSalaryPeriod(referenceDate: Date = new Date()): SalaryPeriod {
    const day = referenceDate.getDate();
    const month = referenceDate.getMonth();
    const year = referenceDate.getFullYear();

    let startDate: Date;
    let endDate: Date;

    if (day >= 21) {
      startDate = new Date(year, month, 21);
      endDate = new Date(year, month + 1, 20);
    } else {
      startDate = new Date(year, month - 1, 21);
      endDate = new Date(year, month, 20);
    }

    const formatDate = (date: Date) => {
      const day = date.getDate();
      const month = date.toLocaleString('en-US', { month: 'short' });
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    };

    return {
      startDate,
      endDate,
      label: `${formatDate(startDate)} to ${formatDate(endDate)}`
    };
  }

  getAvailableSalaryPeriods(count: number = 6): SalaryPeriod[] {
    const periods: SalaryPeriod[] = [];
    let referenceDate = new Date();

    for (let i = 0; i < count; i++) {
      const period = this.getSalaryPeriod(referenceDate);
      periods.push(period);
      
      referenceDate = new Date(period.startDate);
      referenceDate.setDate(referenceDate.getDate() - 1);
    }

    return periods;
  }

  // ========================================================================
  // 🆕 NIGHT SHIFT DEFAULT OT
  // ========================================================================
  
  private calculateNightShiftDefaultOT(
    nightShiftStartTime: string,
    nightShiftEndTime: string
  ): number {
    try {
      const [startHour, startMin] = nightShiftStartTime.split(':').map(Number);
      const [endHour, endMin] = nightShiftEndTime.split(':').map(Number);
      
      let startMinutes = startHour * 60 + startMin;
      let endMinutes = endHour * 60 + endMin;
      
      if (endMinutes < startMinutes) {
        endMinutes += 24 * 60;
      }
      
      const totalMinutes = endMinutes - startMinutes;
      const totalHours = totalMinutes / 60;
      
      // 8h regular + 1h break = 9h standard
      const defaultOTHours = Math.max(0, totalHours - 9);
      
      console.log(`   🌙 Night shift: ${nightShiftStartTime}-${nightShiftEndTime} = ${totalHours}h total, ${defaultOTHours}h default OT`);
      
      return parseFloat(defaultOTHours.toFixed(2));
    } catch (error) {
      console.error('   ❌ Error calculating night shift default OT:', error);
      return 0;
    }
  }

  // ========================================================================
  // 🆕 LATE CHECKOUT OT (COMPLETE REWRITE WITH PROPER LOGIC)
  // ========================================================================
  
  private calculateLateCheckoutOT(
    checkOut: string,
    shiftEnd: string,
    hasApprovedOT: boolean
  ): number {
    try {
      if (!checkOut) return 0;
      
      // ✅ CRITICAL FIX: If employee has approved OT, don't calculate default late checkout OT
      // The approved OT already covers their overtime work
      if (hasApprovedOT) {
        console.log(`   ✅ Has approved OT request - Skipping default late checkout OT calculation`);
        return 0;
      }
      
      const [checkOutHour, checkOutMin] = checkOut.split(':').map(Number);
      const checkOutMinutes = checkOutHour * 60 + checkOutMin;
      
      const [shiftEndHour, shiftEndMin] = shiftEnd.split(':').map(Number);
      const shiftEndMinutes = shiftEndHour * 60 + shiftEndMin;
      
      // If checkout is at or before shift end, no late OT
      if (checkOutMinutes <= shiftEndMinutes) {
        console.log(`   ✅ Checkout at shift end time (${checkOut}) - No late OT`);
        return 0;
      }
      
      // 6 PM = 18:00 = 1080 minutes
      const sixPMMinutes = 18 * 60;
      
      // ✅ RULE 1: If checkout is after 6 PM and NO approved OT request
      // → DO NOT count any late OT (they worked without permission)
      if (checkOutMinutes > sixPMMinutes) {
        console.log(`   ⚠️ Checkout after 6 PM (${checkOut}) with NO OT request - Not counting late OT`);
        return 0;
      }
      
      // ✅ RULE 2: Calculate late OT from shift end to 6 PM ONLY
      // Late OT = min(checkout, 6PM) - shift end
      const otEndMinutes = Math.min(checkOutMinutes, sixPMMinutes);
      const lateOTMinutes = Math.max(0, otEndMinutes - shiftEndMinutes);
      
      if (lateOTMinutes === 0) {
        console.log(`   ✅ No late OT period`);
        return 0;
      }
      
      // ✅ RULE 3: Rounding logic
      // 5:50 PM - 6:10 PM → Round to 6 PM (treat as 6 PM)
      // 5:00 PM - 5:10 PM → Count as 1h only
      
      let finalLateOTHours = 0;
      
      // If checkout is between 5:50 PM (17:50 = 1070 min) and 6:10 PM (18:10 = 1090 min)
      // → Round to exactly 6 PM
      if (checkOutMinutes >= 1070 && checkOutMinutes <= 1090) {
        const roundedEndMinutes = sixPMMinutes; // Round to 6 PM
        finalLateOTHours = (roundedEndMinutes - shiftEndMinutes) / 60;
        console.log(`   🔄 Rounded checkout from ${checkOut} to 18:00 for calculation`);
      }
      // If checkout is between 5:00 PM (17:00 = 1020 min) and 5:10 PM (17:10 = 1030 min)
      // → Count as exactly 1 hour
      else if (checkOutMinutes >= 1020 && checkOutMinutes <= 1030) {
        finalLateOTHours = 1.0;
        console.log(`   🔄 Checkout between 17:00-17:10 (${checkOut}) - Counting as 1h OT`);
      }
      // Otherwise, normal calculation
      else {
        finalLateOTHours = lateOTMinutes / 60;
      }
      
      // ✅ RULE 4: Only count if meaningful (at least 10 minutes = 0.17 hours)
      if (finalLateOTHours < 0.17) {
        console.log(`   ⏰ Late OT too small (${finalLateOTHours.toFixed(2)}h) - not counting`);
        return 0;
      }
      
      console.log(`   ⏰ Late checkout OT: ${checkOut} → ${finalLateOTHours.toFixed(2)}h (shift ends ${shiftEnd}, OT counted until 6 PM)`);
      
      return parseFloat(finalLateOTHours.toFixed(2));
    } catch (error) {
      console.error('   ❌ Error calculating late checkout OT:', error);
      return 0;
    }
  }

  // ========================================================================
  // 🆕 REMOVE DUPLICATE OT SESSIONS
  // ========================================================================
  
  private removeDuplicateOTSessions(sessions: OvertimeSession[]): OvertimeSession[] {
    if (sessions.length <= 1) return sessions;
    
    const uniqueSessions = new Map<string, OvertimeSession>();
    
    sessions.forEach(session => {
      const key = `${session.projectCode}-${session.startTime}-${session.endTime}`;
      
      if (!uniqueSessions.has(key)) {
        uniqueSessions.set(key, session);
      } else {
        console.log(`   ⚠️ Duplicate OT detected: ${session.projectCode} (${session.startTime}-${session.endTime}) - removing duplicate`);
      }
    });
    
    return Array.from(uniqueSessions.values());
  }

  // ========================================================================
  // GET ATTENDANCE RECORD
  // ========================================================================
  
  private async getAttendanceRecord(uuid: string, targetDate: Date): Promise<any> {
    try {
      const year = targetDate.getFullYear();
      const month = String(targetDate.getMonth() + 1).padStart(2, '0');
      const day = String(targetDate.getDate()).padStart(2, '0');
      const dateKey = `${year}-${month}-${day}`;
      
      const recordId = `${uuid}-${dateKey}`;
      
      console.log(`   🔍 Looking for attendance: ${recordId}`);
      
      const recordRef = doc(db, 'Attendance_Records', 'PTSEmployees', 'Records', recordId);
      const recordDoc = await getDoc(recordRef);

      if (recordDoc.exists()) {
        const data = recordDoc.data();
        
        const checkInTimestamp = data.checkIn?.toDate?.() || null;
        const checkOutTimestamp = data.checkOut?.toDate?.() || null;
        
        let validCheckIn = null;
        if (checkInTimestamp) {
          const checkInDate = new Date(checkInTimestamp);
          const checkInDateStr = `${checkInDate.getFullYear()}-${String(checkInDate.getMonth() + 1).padStart(2, '0')}-${String(checkInDate.getDate()).padStart(2, '0')}`;
          
          if (checkInDateStr === dateKey) {
            validCheckIn = this.formatTime(checkInTimestamp);
          }
        }
        
        const checkOut = checkOutTimestamp ? this.formatTime(checkOutTimestamp) : null;
        const checkOutLocation = this.getLocationDisplayName(data);
        
        console.log(`   📝 Attendance: CheckIn=${validCheckIn}, CheckOut=${checkOut}, Location=${checkOutLocation}`);
        
        return {
          checkIn: validCheckIn,
          checkOut: checkOut,
          checkOutLocation: checkOutLocation,
          workStatus: data.workStatus || 'Present',
          noBreak: data.noBreak || false,
          noBreakReason: data.noBreakReason || '',
          noBreakAppliedBy: data.noBreakAppliedBy || ''
        };
      }

      return null;
    } catch (error) {
      console.error('   ❌ Error fetching attendance:', error);
      return null;
    }
  }

  // ========================================================================
  // GET OVERTIME
  // ========================================================================
  
  private async getOvertimeForEmployee(
    masterSheetId: string,
    pin: string,
    uuid: string | null,
    targetDate: Date
  ): Promise<OvertimeSession[]> {
    try {
      const year = targetDate.getFullYear();
      const month = String(targetDate.getMonth() + 1).padStart(2, '0');
      const day = String(targetDate.getDate()).padStart(2, '0');
      const targetDateStr = `${year}-${month}-${day}`;
      
      const numericPin = this.extractPinFromId(pin) || this.extractPinFromId(masterSheetId);
      
      console.log(`\n   🔍 ========== OVERTIME SEARCH ==========`);
      console.log(`   📅 Date: ${targetDateStr}`);
      console.log(`   🆔 MasterSheet ID: ${masterSheetId}`);
      console.log(`   📌 PIN: ${pin} (numeric: ${numericPin})`);
      console.log(`   🔑 UUID: ${uuid || 'N/A'}`);

      const sessions: OvertimeSession[] = [];

      const overtimeQuery = query(
        collection(db, 'overtime_requests'),
        where('status', '==', 'approved')
      );

      const snapshot = await getDocs(overtimeQuery);
      console.log(`   📊 Total approved OT requests: ${snapshot.size}`);
      
      let checkedForDate = 0;

      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        
        let overtimeDate = data.overtimeDate?.toDate?.() || data.startTime?.toDate?.();
        if (!overtimeDate) continue;

        const otDateStr = `${overtimeDate.getFullYear()}-${String(overtimeDate.getMonth() + 1).padStart(2, '0')}-${String(overtimeDate.getDate()).padStart(2, '0')}`;
        
        if (otDateStr !== targetDateStr) continue;
        
        checkedForDate++;

        const requestEmployeeIds: string[] = data.employeeIds || [];
        
        const employeeDetailsIds: string[] = [];
        if (data.employeeDetails && Array.isArray(data.employeeDetails)) {
          data.employeeDetails.forEach((emp: any) => {
            if (emp.id) employeeDetailsIds.push(emp.id);
            if (emp.employeeNumber) employeeDetailsIds.push(emp.employeeNumber);
            if (emp.pin) employeeDetailsIds.push(emp.pin);
          });
        }

        const allRequestIds = [...new Set([...requestEmployeeIds, ...employeeDetailsIds])];

        let isEmployeeInRequest = false;
        let matchedBy = '';

        for (const reqId of allRequestIds) {
          if (reqId === masterSheetId || reqId === pin || reqId === uuid) {
            isEmployeeInRequest = true;
            matchedBy = `direct match: ${reqId}`;
            break;
          }

          if (reqId.toLowerCase() === masterSheetId.toLowerCase() ||
              reqId.toLowerCase() === pin.toLowerCase()) {
            isEmployeeInRequest = true;
            matchedBy = `case-insensitive: ${reqId}`;
            break;
          }

          const reqNumericPin = this.extractPinFromId(reqId);
          if (reqNumericPin && numericPin && reqNumericPin === numericPin) {
            isEmployeeInRequest = true;
            matchedBy = `numeric PIN match: ${reqId} -> ${reqNumericPin}`;
            break;
          }

          if (uuid && reqId === uuid) {
            isEmployeeInRequest = true;
            matchedBy = `UUID match: ${reqId}`;
            break;
          }
        }

        if (!isEmployeeInRequest) {
          if (checkedForDate <= 2) {
            console.log(`      ❌ No match in ${docSnap.id.substring(0, 8)} (${data.projectCode})`);
          }
          continue;
        }

        console.log(`      ✅ MATCH! Request: ${docSnap.id.substring(0, 8)}, Project: ${data.projectCode}`);
        console.log(`         Matched by: ${matchedBy}`);

        const startTime = data.startTime?.toDate?.();
        const endTime = data.endTime?.toDate?.();
        
        if (!startTime || !endTime) {
          console.log(`      ⚠️ Missing start/end time, skipping`);
          continue;
        }
        
        const duration = (endTime.getTime() - startTime.getTime()) / (1000 * 60 * 60);

        sessions.push({
          projectCode: data.projectCode || 'N/A',
          projectName: data.projectName || 'N/A',
          startTime: this.formatTime(startTime),
          endTime: this.formatTime(endTime),
          duration: parseFloat(duration.toFixed(2)),
          status: 'approved'
        });
      }

      console.log(`   📊 Checked ${checkedForDate} requests for ${targetDateStr}`);
      
      const uniqueSessions = this.removeDuplicateOTSessions(sessions);
      
      if (uniqueSessions.length > 0) {
        const totalHours = uniqueSessions.reduce((sum, s) => sum + s.duration, 0);
        console.log(`   🎯 RESULT: ${uniqueSessions.length} unique sessions, ${totalHours.toFixed(2)} hours`);
      } else if (checkedForDate > 0) {
        console.log(`   ⚠️ WARNING: ${checkedForDate} OT requests exist for this date but employee not found`);
      } else {
        console.log(`   ℹ️ No approved OT requests for ${targetDateStr}`);
      }
      
      console.log(`   ========================================\n`);

      return uniqueSessions;
    } catch (error) {
      console.error('   ❌ Error fetching overtime:', error);
      return [];
    }
  }

  // ========================================================================
  // CALCULATE BREAK TIME
  // ========================================================================
  
  private calculateBreakTime(attendanceRecord: any): { display: string; noBreakCount: number } {
    if (!attendanceRecord) {
      return { display: '-', noBreakCount: 0 };
    }
    
    if (attendanceRecord.noBreak) {
      return { display: 'No Break', noBreakCount: 1 };
    }
    
    return { display: '-', noBreakCount: 0 };
  }

  // ========================================================================
  // 🆕 CALCULATE HOURS (WITH MISSING CHECKOUT CHECK + NEW LATE OT LOGIC)
  // ========================================================================
  
  private calculateHours(
    attendanceRecord: any,
    overtimeSessions: OvertimeSession[],
    shiftStart: string,
    shiftEnd: string,
    employee: TimesheetEmployee,
    hasExcessiveIncompleteCheckouts: boolean,
    recordDate: Date
  ): { regular: number; overtime: number; total: number; breakdown: any; isIncomplete: boolean } {
    
    if (!attendanceRecord?.checkIn) {
      return { 
        regular: 0, 
        overtime: 0, 
        total: 0,
        isIncomplete: false,
        breakdown: {
          approvedOT: 0,
          nightShiftDefaultOT: 0,
          lateCheckoutOT: 0,
          noBreakBonus: 0
        }
      };
    }

    // ✅ CHECK: If has check-in but no check-out
    // Don't mark as incomplete if it's today (day isn't over yet)
    const isIncomplete = !attendanceRecord.checkOut && !this.isToday(recordDate);
    
    // ✅ NEW POLICY: If > 3 incomplete checkouts in period, don't count ANY hours
    if (hasExcessiveIncompleteCheckouts && isIncomplete) {
      console.log(`   ⚠️ INCOMPLETE CHECKOUT POLICY: Too many incomplete records (>3) - NOT counting hours for this day`);
      return {
        regular: 0,
        overtime: 0,
        total: 0,
        isIncomplete: true,
        breakdown: {
          approvedOT: 0,
          nightShiftDefaultOT: 0,
          lateCheckoutOT: 0,
          noBreakBonus: 0
        }
      };
    }

    // 1️⃣ REGULAR HOURS (Always 8 hours if present)
    const regularHours = 8;

    // 2️⃣ APPROVED OVERTIME (from requests)
    const approvedOT = overtimeSessions.reduce((sum, session) => sum + session.duration, 0);
    const hasApprovedOT = approvedOT > 0;

    // 3️⃣ NIGHT SHIFT DEFAULT OT (only if has checkout OR within 3 incomplete limit)
    let nightShiftDefaultOT = 0;
    if (employee.isNightShift && employee.nightShiftStartTime && employee.nightShiftEndTime) {
      if (attendanceRecord.checkOut || !hasExcessiveIncompleteCheckouts) {
        nightShiftDefaultOT = this.calculateNightShiftDefaultOT(
          employee.nightShiftStartTime,
          employee.nightShiftEndTime
        );
      } else {
        console.log(`   ⚠️ No checkout and >3 incomplete records - NO night shift default OT`);
      }
    }

    // 4️⃣ LATE CHECKOUT OT (only if actually has checkout AND is day shift)
    let lateCheckoutOT = 0;
    if (!employee.isNightShift && attendanceRecord.checkOut) {
      lateCheckoutOT = this.calculateLateCheckoutOT(
        attendanceRecord.checkOut,
        shiftEnd,
        hasApprovedOT
      );
    }

    // 5️⃣ NO-BREAK BONUS (only if has valid attendance)
    let noBreakBonus = 0;
    if (attendanceRecord.noBreak === true && (attendanceRecord.checkOut || !hasExcessiveIncompleteCheckouts)) {
      noBreakBonus = 1;
    }

    // 🎯 TOTAL OVERTIME
    const totalOvertimeHours = approvedOT + nightShiftDefaultOT + lateCheckoutOT + noBreakBonus;

    // ✅ FIXED: TOTAL HOURS = 8h regular + 1h break + OT
    const totalHours = regularHours + 1 + totalOvertimeHours;

    console.log(`   💰 Hours Breakdown:`);
    console.log(`      Regular: ${regularHours}h`);
    console.log(`      Break: 1h`);
    console.log(`      Approved OT: ${approvedOT}h`);
    console.log(`      Night Shift Default OT: ${nightShiftDefaultOT}h`);
    console.log(`      Late Checkout OT: ${lateCheckoutOT}h`);
    console.log(`      No-Break Bonus: ${noBreakBonus}h`);
    console.log(`      TOTAL OT: ${totalOvertimeHours}h`);
    console.log(`      TOTAL HOURS (incl. break): ${totalHours}h`);
    console.log(`      Incomplete: ${isIncomplete}`);

    return {
      regular: parseFloat(regularHours.toFixed(2)),
      overtime: parseFloat(totalOvertimeHours.toFixed(2)),
      total: parseFloat(totalHours.toFixed(2)),
      isIncomplete: isIncomplete,
      breakdown: {
        approvedOT: parseFloat(approvedOT.toFixed(2)),
        nightShiftDefaultOT: parseFloat(nightShiftDefaultOT.toFixed(2)),
        lateCheckoutOT: parseFloat(lateCheckoutOT.toFixed(2)),
        noBreakBonus: parseFloat(noBreakBonus.toFixed(2))
      }
    };
  }

  // ========================================================================
  // 🆕 GENERATE REMARKS (WITH INCOMPLETE WARNING + NEW LATE OT LOGIC)
  // ========================================================================
  
  private generateRemarks(
    attendanceRecord: any, 
    overtimeSessions: OvertimeSession[],
    hoursBreakdown: any,
    employee: TimesheetEmployee,
    isIncomplete: boolean,
    hasExcessiveIncompleteCheckouts: boolean,
    recordDate: Date
  ): string {
    const remarks: string[] = [];

    // 🚨 PRIORITY: Incomplete checkout warning (but NOT for today)
    if (isIncomplete && hasExcessiveIncompleteCheckouts && !this.isToday(recordDate)) {
      remarks.push(`🚨 INCOMPLETE CHECKOUT - Excessive incomplete records (>3) - Hours NOT counted`);
      return remarks.join(' | ');
    } else if (isIncomplete && !this.isToday(recordDate)) {
      remarks.push(`⚠️ Missing checkout - Within 3 incomplete allowance`);
    }

    // 1️⃣ Approved OT from requests
    if (overtimeSessions.length > 0) {
      const projects = overtimeSessions.map(s => 
        `${s.projectCode} (${s.startTime}-${s.endTime}, ${s.duration}h)`
      ).join(', ');
      remarks.push(`✅ Approved OT: ${projects}`);
    }

    // 2️⃣ Night Shift Default OT
    if (hoursBreakdown.nightShiftDefaultOT > 0) {
      remarks.push(`Night Shift Default OT: ${hoursBreakdown.nightShiftDefaultOT}h (${employee.nightShiftStartTime}-${employee.nightShiftEndTime})`);
    }

    // 3️⃣ Late Checkout OT (ONLY if NO approved OT and has checkout)
    // If there's approved OT, the late checkout OT should be 0 anyway
    if (hoursBreakdown.lateCheckoutOT > 0 && attendanceRecord.checkOut && overtimeSessions.length === 0) {
      const location = attendanceRecord.checkOutLocation || 'Unknown Location';
      remarks.push(`⏰ Default late OT: ${hoursBreakdown.lateCheckoutOT}h until 6 PM (Checkout: ${attendanceRecord.checkOut}, Location: ${location})`);
    }

    // 4️⃣ No-Break Bonus
    if (hoursBreakdown.noBreakBonus > 0) {
      const appliedBy = attendanceRecord.noBreakAppliedBy || 'System';
      remarks.push(`💪 No-break applied by ${appliedBy} - Added ${hoursBreakdown.noBreakBonus}h bonus OT`);
    }

    return remarks.join(' | ');
  }

  // ========================================================================
  // CHECK DISCREPANCIES
  // ========================================================================
  
  private checkDiscrepancies(
    attendanceRecord: any,
    overtimeSessions: OvertimeSession[],
    shiftEnd: string
  ): string {
    if (!attendanceRecord?.checkOut) return '';

    const discrepancies: string[] = [];

    const [shiftEndHour, shiftEndMin] = shiftEnd.split(':').map(Number);
    const [checkOutHour, checkOutMin] = attendanceRecord.checkOut.split(':').map(Number);
    
    const shiftEndMinutes = shiftEndHour * 60 + shiftEndMin;
    const checkOutMinutes = checkOutHour * 60 + checkOutMin;

    if (checkOutMinutes < shiftEndMinutes) {
      const diffMinutes = shiftEndMinutes - checkOutMinutes;
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      
      if (hours > 0 || minutes > 0) {
        const timeStr = hours > 0 
          ? (minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`)
          : `${minutes}m`;
        discrepancies.push(`Left ${timeStr} before shift end (${shiftEnd})`);
      }
    }

    for (const session of overtimeSessions) {
      const [otEndHour, otEndMin] = session.endTime.split(':').map(Number);
      const otEndMinutes = otEndHour * 60 + otEndMin;

      if (checkOutMinutes < otEndMinutes) {
        const diffMinutes = otEndMinutes - checkOutMinutes;
        const hours = Math.floor(diffMinutes / 60);
        const minutes = diffMinutes % 60;
        
        if (hours > 0 || minutes > 0) {
          const timeStr = hours > 0 
            ? (minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`)
            : `${minutes}m`;
          discrepancies.push(`Left ${timeStr} early from OT ${session.projectCode} (${session.endTime})`);
        }
      }
    }

    return discrepancies.join(' | ');
  }

  // ========================================================================
  // 🆕 GENERATE TIMESHEET (WITH ALL NEW LOGIC)
  // ========================================================================
  
  async generateTimesheet(
    employee: TimesheetEmployee,
    periodStart: Date,
    periodEnd: Date
  ): Promise<TimesheetData> {
    try {
      console.log('\n📊 ========== GENERATING TIMESHEET ==========');
      console.log('📊 Employee:', employee.name, '(PIN:', employee.pin, ', ID:', employee.id, ')');
      console.log('📊 Work Timing:', employee.workTiming);
      console.log('📊 Night Shift:', employee.isNightShift);
      if (employee.isNightShift) {
        console.log('📊 Night Shift Hours:', employee.nightShiftStartTime, '-', employee.nightShiftEndTime);
      }
      console.log('📊 Period:', this.formatDateForDisplay(periodStart), 'to', this.formatDateForDisplay(periodEnd));

      await this.buildPinToUUIDMap();
      const uuid = this.getUUIDFromPin(employee.pin);

      console.log('📊 UUID from PIN:', uuid);

      // 🆕 FIRST PASS: Count incomplete checkouts (excluding today)
      console.log('\n🔍 ========== FIRST PASS: Counting Incomplete Checkouts ==========');
      let incompleteCheckoutsCount = 0;
      const tempRecords: any[] = [];
      const currentDateTemp = new Date(periodStart);

      while (currentDateTemp <= periodEnd) {
        const attendanceRecord = uuid 
          ? await this.getAttendanceRecord(uuid, currentDateTemp)
          : null;
        
        // ✅ Only count as incomplete if it's NOT today
        if (attendanceRecord?.checkIn && !attendanceRecord.checkOut && !this.isToday(currentDateTemp)) {
          incompleteCheckoutsCount++;
          console.log(`   ⚠️ Incomplete: ${this.formatDateForDisplay(currentDateTemp)} - CheckIn: ${attendanceRecord.checkIn}, CheckOut: None`);
        }
        
        tempRecords.push({
          date: new Date(currentDateTemp),
          attendance: attendanceRecord
        });
        
        currentDateTemp.setDate(currentDateTemp.getDate() + 1);
      }

      console.log(`\n📊 Total Incomplete Checkouts (excluding today): ${incompleteCheckoutsCount}`);
      const hasExcessiveIncompleteCheckouts = incompleteCheckoutsCount > 3;
      
      if (hasExcessiveIncompleteCheckouts) {
        console.log(`🚨 WARNING: More than 3 incomplete checkouts detected! Incomplete days will NOT be counted.`);
      } else {
        console.log(`✅ Within acceptable limit (≤3 incomplete checkouts)`);
      }

      // 🆕 SECOND PASS: Generate actual records with policy applied
      console.log('\n📊 ========== SECOND PASS: Generating Records ==========');
      const records: DailyTimesheetRecord[] = [];

      let totalPresent = 0;
      let totalAbsent = 0;
      let totalLeaves = 0;
      let totalNoBreakDays = 0;
      let totalOvertimeDays = 0;
      let totalRegularHours = 0;
      let totalOvertimeHours = 0;
      
      let totalApprovedOT = 0;
      let totalNightShiftDefaultOT = 0;
      let totalLateCheckoutOT = 0;
      let totalNoBreakBonus = 0;

      for (const tempRecord of tempRecords) {
        const currentDate = tempRecord.date;
        const attendanceRecord = tempRecord.attendance;
        const dateString = this.formatDateForDisplay(currentDate);
        
        console.log(`\n📅 Processing Date: ${dateString} ${this.isToday(currentDate) ? '(TODAY)' : ''}`);
        
        const overtimeSessions = await this.getOvertimeForEmployee(
          employee.id,
          employee.pin,
          uuid,
          currentDate
        );

        const breakInfo = this.calculateBreakTime(attendanceRecord);

        const hours = this.calculateHours(
          attendanceRecord,
          overtimeSessions,
          employee.shiftStart,
          employee.shiftEnd,
          employee,
          hasExcessiveIncompleteCheckouts,
          currentDate
        );

        const remarks = this.generateRemarks(
          attendanceRecord, 
          overtimeSessions,
          hours.breakdown,
          employee,
          hours.isIncomplete,
          hasExcessiveIncompleteCheckouts,
          currentDate
        );
        
        const discrepancy = this.checkDiscrepancies(
          attendanceRecord, 
          overtimeSessions, 
          employee.shiftEnd
        );

        console.log(`   ✅ Record: CheckIn=${attendanceRecord?.checkIn || 'None'}, CheckOut=${attendanceRecord?.checkOut || 'None'}, Total Hours=${hours.total}h, Incomplete=${hours.isIncomplete}`);

        const record: DailyTimesheetRecord = {
          date: new Date(currentDate),
          dateString,
          checkIn: attendanceRecord?.checkIn || null,
          checkOut: attendanceRecord?.checkOut || null,
          checkOutLocation: attendanceRecord?.checkOutLocation,
          breakTime: breakInfo.display,
          noBreakCount: breakInfo.noBreakCount,
          overtimeSessions,
          remarks,
          discrepancy,
          regularHours: hours.regular,
          overtimeHours: hours.overtime,
          totalHours: hours.total,
          isIncomplete: hours.isIncomplete,
          overtimeBreakdown: hours.breakdown
        };

        records.push(record);

        if (attendanceRecord?.checkIn) {
          if (hours.regular > 0) {
            totalPresent++;
            totalRegularHours += hours.regular;
          }
        } else if (attendanceRecord?.workStatus === 'Leave') {
          totalLeaves++;
        } else {
          totalAbsent++;
        }

        if (breakInfo.noBreakCount > 0) totalNoBreakDays++;
        if (hours.overtime > 0) {
          totalOvertimeDays++;
          totalOvertimeHours += hours.overtime;
          
          totalApprovedOT += hours.breakdown.approvedOT;
          totalNightShiftDefaultOT += hours.breakdown.nightShiftDefaultOT;
          totalLateCheckoutOT += hours.breakdown.lateCheckoutOT;
          totalNoBreakBonus += hours.breakdown.noBreakBonus;
        }
      }

      console.log('\n📊 ========== TIMESHEET COMPLETE ==========');
      console.log(`📊 Incomplete Checkouts: ${incompleteCheckoutsCount} (Policy: ${hasExcessiveIncompleteCheckouts ? 'STRICT - Hours not counted' : 'LENIENT - Hours counted'})`);
      console.log(`📊 Total OT Days: ${totalOvertimeDays}, Total OT Hours: ${totalOvertimeHours.toFixed(2)}`);
      console.log(`📊 OT Breakdown:`);
      console.log(`   - Approved OT: ${totalApprovedOT.toFixed(2)}h`);
      console.log(`   - Night Shift Default OT: ${totalNightShiftDefaultOT.toFixed(2)}h`);
      console.log(`   - Late Checkout OT: ${totalLateCheckoutOT.toFixed(2)}h`);
      console.log(`   - No-Break Bonus: ${totalNoBreakBonus.toFixed(2)}h`);

      return {
        employee,
        periodStart,
        periodEnd,
        records,
        incompleteCheckoutsCount,
        summary: {
          totalWorkDays: records.length,
          totalPresent,
          totalAbsent,
          totalLeaves,
          totalNoBreakDays,
          totalOvertimeDays,
          totalRegularHours: parseFloat(totalRegularHours.toFixed(2)),
          totalOvertimeHours: parseFloat(totalOvertimeHours.toFixed(2)),
          totalHours: parseFloat((totalRegularHours + totalOvertimeHours + totalPresent).toFixed(2)),
          totalApprovedOT: parseFloat(totalApprovedOT.toFixed(2)),
          totalNightShiftDefaultOT: parseFloat(totalNightShiftDefaultOT.toFixed(2)),
          totalLateCheckoutOT: parseFloat(totalLateCheckoutOT.toFixed(2)),
          totalNoBreakBonus: parseFloat(totalNoBreakBonus.toFixed(2)),
          incompleteCheckouts: incompleteCheckoutsCount
        }
      };
    } catch (error) {
      console.error('❌ Error generating timesheet:', error);
      throw error;
    }
  }

  // ========================================================================
  // EXPORT TO EXCEL
  // ========================================================================
  
  async exportTimesheetToExcel(timesheetData: TimesheetData): Promise<void> {
    try {
      console.log('📥 Exporting timesheet to Excel...');

      const workbook = XLSX.utils.book_new();
      const excelData: any[] = [];

      excelData.push(['PHOENICIAN TECHNICAL SERVICES LLC']);
      excelData.push(['EMPLOYEE TIMESHEET']);
      excelData.push([]);
      
      excelData.push(['Employee Name:', timesheetData.employee.name]);
      excelData.push(['PIN/AUX:', timesheetData.employee.pin]);
      excelData.push(['Designation:', timesheetData.employee.designation]);
      excelData.push(['Department:', timesheetData.employee.department]);
      excelData.push(['Work Timing:', timesheetData.employee.workTiming]);
      excelData.push(['Shift Type:', timesheetData.employee.isNightShift ? 'Night Shift' : 'Day Shift']);
      excelData.push(['Period:', `${this.formatDateForDisplay(timesheetData.periodStart)} to ${this.formatDateForDisplay(timesheetData.periodEnd)}`]);
      excelData.push(['Incomplete Checkouts:', `${timesheetData.incompleteCheckoutsCount} ${timesheetData.incompleteCheckoutsCount > 3 ? '(⚠️ EXCEEDS LIMIT - Incomplete days not counted)' : '(✅ Within limit)'}`]);
      excelData.push([]);
      
      excelData.push(['SUMMARY']);
      excelData.push(['Total Work Days:', timesheetData.summary.totalWorkDays]);
      excelData.push(['Total Present:', timesheetData.summary.totalPresent]);
      excelData.push(['Total Absent:', timesheetData.summary.totalAbsent]);
      excelData.push(['Total No-Break Days:', timesheetData.summary.totalNoBreakDays]);
      excelData.push(['Total Overtime Days:', timesheetData.summary.totalOvertimeDays]);
      excelData.push(['Total Regular Hours:', timesheetData.summary.totalRegularHours]);
      excelData.push([]);
      
      excelData.push(['OVERTIME BREAKDOWN']);
      excelData.push(['Approved OT (from requests):', timesheetData.summary.totalApprovedOT + ' hours']);
      excelData.push(['Night Shift Default OT:', timesheetData.summary.totalNightShiftDefaultOT + ' hours']);
      excelData.push(['Late Checkout OT (shift end to 6 PM):', timesheetData.summary.totalLateCheckoutOT + ' hours']);
      excelData.push(['No-Break Bonus:', timesheetData.summary.totalNoBreakBonus + ' hours']);
      excelData.push(['Total Overtime Hours:', timesheetData.summary.totalOvertimeHours + ' hours']);
      excelData.push([]);
      excelData.push(['TOTAL HOURS (Regular + Break + OT):', timesheetData.summary.totalHours + ' hours']);
      excelData.push([]);

      excelData.push([
        'Date', 'Check-In', 'Check-Out', 'Location', 'Regular Hours', 'Break', 'No Break',
        'Overtime Details', 'OT Hours', 'Total Hours', 'Remarks', 'Discrepancy'
      ]);

      timesheetData.records.forEach(record => {
        const overtimeText = record.overtimeSessions
          .map(ot => `${ot.projectCode}: ${ot.startTime}-${ot.endTime} (${ot.duration}h)`)
          .join(' | ');

        excelData.push([
          record.dateString,
          record.checkIn || '-',
          record.checkOut || '-',
          record.checkOutLocation || '-',
          record.regularHours,
          record.breakTime,
          record.noBreakCount,
          overtimeText || '-',
          record.overtimeHours,
          record.totalHours,
          record.remarks,
          record.discrepancy
        ]);
      });

      const worksheet = XLSX.utils.aoa_to_sheet(excelData);

      worksheet['!cols'] = [
        { wch: 15 }, { wch: 10 }, { wch: 10 }, { wch: 20 }, { wch: 12 }, { wch: 12 },
        { wch: 10 }, { wch: 50 }, { wch: 10 }, { wch: 12 }, { wch: 60 }, { wch: 40 }
      ];

      worksheet['!merges'] = [
        { s: { r: 0, c: 0 }, e: { r: 0, c: 11 } },
        { s: { r: 1, c: 0 }, e: { r: 1, c: 11 } }
      ];

      XLSX.utils.book_append_sheet(workbook, worksheet, 'Timesheet');

      const filename = `Timesheet_${timesheetData.employee.pin}_${this.formatDateForFirestore(timesheetData.periodStart)}_to_${this.formatDateForFirestore(timesheetData.periodEnd)}.xlsx`;
      
      XLSX.writeFile(workbook, filename);
      console.log('✅ Excel exported:', filename);
    } catch (error) {
      console.error('❌ Export error:', error);
      throw error;
    }
  }

  // ========================================================================
  // UTILITY METHODS
  // ========================================================================
  
  private formatDateForDisplay(date: Date): string {
    const day = date.getDate();
    const month = date.toLocaleString('en-US', { month: 'short' });
    const year = date.getFullYear();
    return `${day.toString().padStart(2, '0')}-${month}-${year}`;
  }

  private formatDateForFirestore(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private formatTime(date: Date): string {
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  }

  clearCache(): void {
    this.pinToUUIDMap.clear();
    this.uuidToPinMap.clear();
    this.isMapBuilt = false;
    console.log('✅ Cache cleared');
  }
}

export const timesheetService = new TimesheetService();